"use client";

import { useState, useTransition } from "react";
import { trackEvent } from "@/lib/analytics";
import { Asterisk } from "./Asterisk";

const RECIPIENT = "hello@evokeadvisory.au";

export function ContactMailtoForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pending, startTransition] = useTransition();

  const canSubmit = name.trim().length > 0 && message.trim().length > 0 && !pending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    startTransition(async () => {
      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim(),
            subject: subject.trim(),
            message: message.trim(),
          }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setError(data.error ?? "Something went wrong. Please try again.");
          return;
        }
        trackEvent("contact_form_submit", {
          form_location: "/contact",
          has_reply_email: email.trim().length > 0,
        });
        setSuccess(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Network error");
      }
    });
  }

  if (success) {
    return (
      <div
        className="mb-12 border px-6 py-7"
        style={{
          maxWidth: "44ch",
          borderColor: "var(--rule-on-olive)",
          background: "color-mix(in srgb, var(--lime) 8%, transparent)",
        }}
      >
        <div className="mb-3 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lime)" }}>
          <Asterisk size={14} color="var(--lime)" />
          <span>Sent</span>
        </div>
        <p className="m-0 font-serif" style={{ fontSize: 18, lineHeight: 1.5, color: "var(--warm)" }}>
          Thank you. Your message is on its way to {RECIPIENT}. Anna replies personally, usually within one business day.
        </p>
      </div>
    );
  }

  const inputBase =
    "w-full border bg-transparent px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-warm/40 focus:border-lime disabled:opacity-60";
  const inputStyle: React.CSSProperties = {
    color: "var(--warm)",
    borderColor: "var(--rule-on-olive)",
  };

  return (
    <form
      onSubmit={onSubmit}
      className="mb-12 grid gap-4"
      style={{ maxWidth: "44ch" }}
      noValidate
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lime)" }}>
            Name
          </span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
            placeholder="Your name"
            className={inputBase}
            style={inputStyle}
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lime)" }}>
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            placeholder="name@organisation.com.au"
            className={inputBase}
            style={inputStyle}
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lime)" }}>
          Subject
        </span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={pending}
          placeholder="What's this about? (optional)"
          className={inputBase}
          style={inputStyle}
        />
      </label>

      <label className="block">
        <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lime)" }}>
          Message
        </span>
        <textarea
          required
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={pending}
          placeholder="Tell me what you're working through."
          className={`${inputBase} resize-y`}
          style={inputStyle}
        />
      </label>

      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-2 inline-flex items-center justify-center gap-3 border px-6 py-4 text-[13px] font-semibold uppercase tracking-[0.04em] transition-opacity disabled:opacity-50"
        style={{
          background: "var(--lime)",
          color: "var(--olive)",
          borderColor: "var(--lime)",
        }}
      >
        <Asterisk size={14} color="var(--olive)" />
        {pending ? "Sending…" : "Send message"}
      </button>

      {error ? (
        <p
          className="m-0 text-[13px]"
          style={{ color: "color-mix(in srgb, var(--pink) 90%, white)" }}
        >
          {error}
        </p>
      ) : null}
    </form>
  );
}
